import {
  addDoc,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import React, { createContext, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { db } from '../firebase_config';
import { useAuth } from './AuthContext';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // State for all data
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [messages, setMessages] = useState([]);

  // Helper function to get user ID consistently
  const getCurrentUserId = () => {
    return currentUser?.id || currentUser?.uid || null;
  };

  const getCurrentUserName = () => {
    return currentUser?.name || currentUser?.displayName || 'Unknown User';
  };

  // Load initial data when user logs in
  useEffect(() => {
    if (currentUser) {
      loadAllData();
    } else {
      // Clear data when user logs out
      setUsers([]);
      setProjects([]);
      setTasks([]);
      setAttendance([]);
      setMessages([]);
      setLoading(false);
    }
  }, [currentUser]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      
      // Load all data in parallel
      await Promise.all([
        loadUsers(),
        loadProjects(),
        loadTasks(),
        loadAttendance(),
        loadMessages()
      ]);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Load Users
  const loadUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Load Projects
  const loadProjects = async () => {
    try {
      const projectsSnapshot = await getDocs(collection(db, 'projects'));
      const projectsData = projectsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  // Load Tasks
  const loadTasks = async () => {
    try {
      const tasksSnapshot = await getDocs(collection(db, 'tasks'));
      const tasksData = tasksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTasks(tasksData);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  // Load Attendance
  const loadAttendance = async () => {
    try {
      const attendanceSnapshot = await getDocs(collection(db, 'attendance'));
      const attendanceData = attendanceSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAttendance(attendanceData);
    } catch (error) {
      console.error('Error loading attendance:', error);
    }
  };

  // Load Messages
  const loadMessages = async () => {
    try {
      // console.log('🔥 Loading messages from Firebase...');
      
      const messagesSnapshot = await getDocs(collection(db, 'messages'));
      const messagesData = messagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // console.log('✅ Loaded messages:', messagesData.length);
      setMessages(messagesData);
    } catch (error) {
      // console.error('❌ Error loading messages:', error);
    }
  };

  // PROJECT FUNCTIONS
  const createProject = async (projectData) => {
    try {
      const userId = getCurrentUserId();
      const userName = getCurrentUserName();
      
      if (!userId) {
        throw new Error('User not properly authenticated');
      }
      
      const newProject = {
        ...projectData,
        managerId: userId,
        managerName: userName,
        createdBy: userId,
        createdByName: userName,
        teamMembers: [], // Initialize empty team
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'projects'), newProject);
      const projectWithId = { 
        id: docRef.id, 
        ...newProject,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      setProjects(prev => [projectWithId, ...prev]);
      toast.success('Project created successfully!');
      return projectWithId;
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project: ' + error.message);
      throw error;
    }
  };

  const updateProject = async (projectId, updates) => {
    try {
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(doc(db, 'projects', projectId), updateData);
      
      setProjects(prev => prev.map(project => 
        project.id === projectId ? { ...project, ...updates } : project
      ));
      
      toast.success('Project updated successfully!');
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Failed to update project');
      throw error;
    }
  };

  // TASK FUNCTIONS WITH ROLE RESTRICTIONS
  const createTask = async (taskData) => {
    try {
      const userId = getCurrentUserId();
      const userName = getCurrentUserName();
      
      if (!userId) {
        throw new Error('User not properly authenticated');
      }
      
      const assigneeUser = users.find(u => u.id === taskData.assigneeId);
      const project = projects.find(p => p.id === taskData.projectId);
      
      const newTask = {
        ...taskData,
        assigneeName: assigneeUser?.name || 'Unknown',
        projectName: project?.name || 'Unknown Project',
        createdBy: userId,
        createdByName: userName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'tasks'), newTask);
      const taskWithId = { 
        id: docRef.id, 
        ...newTask,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      setTasks(prev => [taskWithId, ...prev]);
      toast.success('Task created successfully!');
      return taskWithId;
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task: ' + error.message);
      throw error;
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const userId = getCurrentUserId();
      const task = tasks.find(t => t.id === taskId);
      
      if (!task) {
        throw new Error('Task not found');
      }
      
      // Role-based restrictions
      if (updates.status === 'completed' && 
          currentUser?.role !== 'owner' && 
          currentUser?.role !== 'project_manager') {
        throw new Error('Only project managers can mark tasks as completed');
      }
      
      // Employees can only start their own tasks
      if (currentUser?.role === 'employee' && 
          task.assigneeId !== userId && 
          updates.status) {
        throw new Error('You can only update your own tasks');
      }
      
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: userId
      };
      
      await updateDoc(doc(db, 'tasks', taskId), updateData);
      
      setTasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, ...updates } : task
      ));
      
      toast.success('Task updated successfully!');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task: ' + error.message);
      throw error;
    }
  };

  // TEAM MANAGEMENT FUNCTIONS
  const assignEmployeeToProject = async (projectId, employeeId) => {
    try {
      const userId = getCurrentUserId();
      
      if (currentUser?.role !== 'owner') {
        throw new Error('Only owners can assign employees to projects');
      }
      
      // Get current project
      const project = projects.find(p => p.id === projectId);
      const employee = users.find(u => u.id === employeeId);
      
      if (!project || !employee) {
        throw new Error('Project or employee not found');
      }
      
      // Update project with team member
      const currentTeam = project.teamMembers || [];
      const updatedTeam = [...currentTeam.filter(id => id !== employeeId), employeeId];
      
      await updateDoc(doc(db, 'projects', projectId), {
        teamMembers: updatedTeam,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, teamMembers: updatedTeam } : p
      ));
      
      toast.success(`${employee.name} assigned to ${project.name}`);
    } catch (error) {
      console.error('Error assigning employee to project:', error);
      toast.error('Failed to assign employee: ' + error.message);
      throw error;
    }
  };

  const removeEmployeeFromProject = async (projectId, employeeId) => {
    try {
      if (currentUser?.role !== 'owner') {
        throw new Error('Only owners can remove employees from projects');
      }
      
      const project = projects.find(p => p.id === projectId);
      const employee = users.find(u => u.id === employeeId);
      
      const updatedTeam = (project.teamMembers || []).filter(id => id !== employeeId);
      
      await updateDoc(doc(db, 'projects', projectId), {
        teamMembers: updatedTeam,
        updatedAt: serverTimestamp()
      });
      
      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, teamMembers: updatedTeam } : p
      ));
      
      toast.success(`${employee.name} removed from ${project.name}`);
    } catch (error) {
      console.error('Error removing employee from project:', error);
      toast.error('Failed to remove employee: ' + error.message);
      throw error;
    }
  };

// ENHANCED TEAM DATA FOR OWNERS AND PROJECT MANAGERS
const getMyTeamData = () => {
  const getCurrentUserId = () => {
    return currentUser?.id || currentUser?.uid;
  };

  const userId = getCurrentUserId();
  const userRole = currentUser?.role;
  
  if (!userId) {
    return {
      myProjects: [],
      teamMembers: [],
      teamAttendance: [],
      teamTasks: []
    };
  }

  let myProjects = [];
  let teamMemberIds = new Set();

  if (userRole === 'owner') {
    // OWNER: See ALL projects and their teams
    myProjects = projects;
    
    // Get all team members from all projects
    projects.forEach(project => {
      if (project.teamMembers && Array.isArray(project.teamMembers)) {
        project.teamMembers.forEach(memberId => {
          teamMemberIds.add(memberId);
        });
      }
      // Also include project managers as team members
      if (project.managerId) {
        teamMemberIds.add(project.managerId);
      }
    });
    
  } else if (userRole === 'project_manager') {
    // PROJECT MANAGER: See projects they created OR are assigned to manage
    myProjects = projects.filter(project => 
      project.createdBy === userId ||  // Projects they created
      project.managerId === userId     // Projects assigned to them by owner
    );
    
    // Get team members only from their projects
    myProjects.forEach(project => {
      if (project.teamMembers && Array.isArray(project.teamMembers)) {
        project.teamMembers.forEach(memberId => {
          teamMemberIds.add(memberId);
        });
      }
    });
    
  } else {
    // EMPLOYEES: Only see projects they're part of
    myProjects = projects.filter(project => 
      project.teamMembers && project.teamMembers.includes(userId)
    );
    
    // Only include themselves and other team members from their projects
    myProjects.forEach(project => {
      if (project.teamMembers && Array.isArray(project.teamMembers)) {
        project.teamMembers.forEach(memberId => {
          teamMemberIds.add(memberId);
        });
      }
    });
  }

  // Get team member user objects
  const teamMembers = users.filter(user => teamMemberIds.has(user.id));

  // Get attendance records for team members
  const teamAttendance = attendance.filter(record => 
    teamMemberIds.has(record.userId)
  );

  // Get tasks from managed projects
  const myProjectIds = myProjects.map(p => p.id);
  let teamTasks = [];

  if (userRole === 'owner') {
    // Owner sees all tasks
    teamTasks = tasks;
  } else {
    // PM sees tasks from their projects or tasks assigned to their team
    teamTasks = tasks.filter(task => 
      myProjectIds.includes(task.projectId) || // Tasks from their projects
      teamMemberIds.has(task.assigneeId) ||    // Tasks assigned to their team
      task.createdBy === userId                // Tasks they created
    );
  }

  // console.log(`Team Data for ${userRole}:`, {
  //   userId,
  //   myProjects: myProjects.length,
  //   teamMembers: teamMembers.length,
  //   teamTasks: teamTasks.length,
  //   teamAttendance: teamAttendance.length
  // });

  return {
    myProjects,
    teamMembers,
    teamAttendance,
    teamTasks
  };
};


  // ATTENDANCE FUNCTIONS
  const checkIn = async () => {
    try {
      const userId = getCurrentUserId();
      const userName = getCurrentUserName();
      
      if (!userId) {
        toast.error('User not properly authenticated');
        return;
      }
      
      const today = new Date().toISOString().split('T')[0];
      const now = new Date();
      const checkInTime = now.toTimeString().slice(0, 5);
      
      // Check if already checked in today
      const existingRecord = attendance.find(
        record => record.userId === userId && record.date === today
      );
      
      if (existingRecord) {
        toast.error('Already checked in today!');
        return;
      }
      
      const attendanceRecord = {
        userId: userId,
        userName: userName,
        date: today,
        checkIn: checkInTime,
        checkOut: null,
        status: 'present',
        hoursWorked: 0,
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'attendance'), attendanceRecord);
      const recordWithId = { 
        id: docRef.id, 
        ...attendanceRecord,
        createdAt: new Date()
      };
      
      setAttendance(prev => [recordWithId, ...prev]);
      toast.success(`Checked in at ${checkInTime}`);
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error('Failed to check in: ' + error.message);
    }
  };

  const checkOut = async () => {
    try {
      const userId = getCurrentUserId();
      
      if (!userId) {
        toast.error('User not properly authenticated');
        return;
      }
      
      const today = new Date().toISOString().split('T')[0];
      const now = new Date();
      const checkOutTime = now.toTimeString().slice(0, 5);
      
      const existingRecord = attendance.find(
        record => record.userId === userId && record.date === today && !record.checkOut
      );
      
      if (!existingRecord) {
        toast.error('No check-in record found for today!');
        return;
      }
      
      // Calculate hours worked
      const checkInTime = new Date(`${today}T${existingRecord.checkIn}`);
      const checkOutTimeDate = new Date(`${today}T${checkOutTime}`);
      const hoursWorked = Math.round((checkOutTimeDate - checkInTime) / (1000 * 60 * 60) * 100) / 100;
      
      const updates = {
        checkOut: checkOutTime,
        hoursWorked: hoursWorked,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(doc(db, 'attendance', existingRecord.id), updates);
      
      setAttendance(prev => prev.map(record => 
        record.id === existingRecord.id ? { ...record, ...updates } : record
      ));
      
      toast.success(`Checked out at ${checkOutTime}. Hours worked: ${hoursWorked}`);
    } catch (error) {
      console.error('Error checking out:', error);
      toast.error('Failed to check out: ' + error.message);
    }
  };

  // MESSAGE FUNCTIONS
  const sendMessage = async (receiverId, content, subject = 'No Subject') => {
    try {
      const userId = getCurrentUserId();
      const userName = getCurrentUserName();
      
      if (!userId) {
        throw new Error('User not properly authenticated');
      }
      
      const receiver = users.find(u => u.id === receiverId);
      
      const messageData = {
        senderId: userId,
        senderName: userName,
        receiverId: receiverId,
        receiverName: receiver?.name || 'Unknown',
        subject: subject,
        content: content,
        read: false,
        timestamp: new Date().toISOString() // Use simple timestamp instead of serverTimestamp
      };
      
      // console.log('🔥 Saving message to Firebase:', messageData);
      
      const docRef = await addDoc(collection(db, 'messages'), messageData);
      // console.log('✅ Message saved with ID:', docRef.id);
      
      const messageWithId = { 
        id: docRef.id, 
        ...messageData
      };
      
      setMessages(prev => [messageWithId, ...prev]);
      toast.success('Message sent successfully!');
      return messageWithId;
      
    } catch (error) {
      console.error('❌ Error saving message:', error);
      toast.error('Failed to send message: ' + error.message);
      throw error;
    }
  };
  
  const markMessageAsRead = async (messageId) => {
    try {
      await updateDoc(doc(db, 'messages', messageId), {
        read: true,
        readAt: new Date().toISOString()
      });
      
      setMessages(prev => prev.map(message => 
        message.id === messageId ? { ...message, read: true } : message
      ));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };
  

  // EXPORT FUNCTIONS
  const exportProjects = () => {
    try {
      const csvContent = "data:text/csv;charset=utf-8," 
        + "Name,Description,Status,Manager,Start Date,End Date,Progress\n"
        + projects.map(p => 
            `"${p.name || ''}","${p.description || ''}","${p.status || ''}","${p.managerName || ''}","${p.startDate || ''}","${p.endDate || ''}","${p.progress || 0}%"`
          ).join("\n");
      
      const link = document.createElement("a");
      link.setAttribute("href", encodeURI(csvContent));
      link.setAttribute("download", "projects.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Projects exported successfully!');
    } catch (error) {
      console.error('Error exporting projects:', error);
      toast.error('Failed to export projects');
    }
  };

  const exportTasks = () => {
    try {
      const csvContent = "data:text/csv;charset=utf-8," 
        + "Title,Description,Assignee,Project,Status,Priority,Due Date\n"
        + tasks.map(t => 
            `"${t.title || ''}","${t.description || ''}","${t.assigneeName || ''}","${t.projectName || ''}","${t.status || ''}","${t.priority || ''}","${t.dueDate || ''}"`
          ).join("\n");
      
      const link = document.createElement("a");
      link.setAttribute("href", encodeURI(csvContent));
      link.setAttribute("download", "tasks.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Tasks exported successfully!');
    } catch (error) {
      console.error('Error exporting tasks:', error);
      toast.error('Failed to export tasks');
    }
  };

  const exportAttendance = () => {
    try {
      const csvContent = "data:text/csv;charset=utf-8," 
        + "Employee,Date,Check In,Check Out,Hours Worked,Status\n"
        + attendance.map(a => 
            `"${a.userName || ''}","${a.date || ''}","${a.checkIn || ''}","${a.checkOut || '-'}","${a.hoursWorked || 0}","${a.status || ''}"`
          ).join("\n");
      
      const link = document.createElement("a");
      link.setAttribute("href", encodeURI(csvContent));
      link.setAttribute("download", "attendance.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Attendance exported successfully!');
    } catch (error) {
      console.error('Error exporting attendance:', error);
      toast.error('Failed to export attendance');
    }
  };

  const exportEmployees = () => {
    try {
      const csvContent = "data:text/csv;charset=utf-8," 
        + "Name,Email,Role,Department,Join Date,Status\n"
        + users.map(u => 
            `"${u.name || ''}","${u.email || ''}","${u.role || ''}","${u.department || ''}","${u.joinDate || ''}","${u.isActive ? 'Active' : 'Inactive'}"`
          ).join("\n");
      
      const link = document.createElement("a");
      link.setAttribute("href", encodeURI(csvContent));
      link.setAttribute("download", "employees.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Employees exported successfully!');
    } catch (error) {
      console.error('Error exporting employees:', error);
      toast.error('Failed to export employees');
    }
  };

  const value = {
    // Data
    users,
    projects,
    tasks,
    attendance,
    messages,
    loading,
    
    // Project functions
    createProject,
    updateProject,
    
    // Task functions
    createTask,
    updateTask,
    
    // Team management functions
    assignEmployeeToProject,
    removeEmployeeFromProject,
    getMyTeamData,
    
    // Attendance functions
    checkIn,
    checkOut,
    
    // Message functions
    sendMessage,
    markMessageAsRead,
    
    // Export functions
    exportProjects,
    exportTasks,
    exportAttendance,
    exportEmployees,
    
    // Reload data
    loadAllData,
    loadMessages
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
